import { RolodexEntryQueryBody } from 'features/rolodex'
import uniq from 'lodash/uniq'
import { RolodexEntryType, RolodexHistogramFilter } from 'types'

/**
 * Combine a Rolodex query with the panel-level `additionalRolodexFilters`
 * a `DrilldownTable` may apply. Returns a new query whose `filters` AND
 * `links` carry the additional predicates and the entry types they
 * target.
 *
 * Bundling these into one operation closes a footgun: a Rolodex histogram
 * filter that targets a type which is not present in the query's `links`
 * silently matches zero rows, so any caller that picks up the additional
 * filters has to remember to also link their types. Doing it here means
 * downstream consumers cannot get one without the other.
 */
export function mergeAdditionalRolodexFilters(
  query: RolodexEntryQueryBody,
  additionalRolodexFilters: ReadonlyArray<RolodexHistogramFilter> | undefined
): RolodexEntryQueryBody {
  if (!additionalRolodexFilters?.length) return query

  const baseFilters = (query.filters ??
    []) as ReadonlyArray<RolodexHistogramFilter>
  const additionalFilterTypes = additionalRolodexFilters
    .map((f) => f.type)
    .filter((t): t is RolodexEntryType => !!t)

  return {
    ...query,
    filters: [...baseFilters, ...additionalRolodexFilters],
    links: uniq([
      ...(query.links ?? []),
      ...additionalFilterTypes,
    ]) as RolodexEntryType[],
  }
}
